import { Component, computed, effect, inject, input, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslatePipe } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../core/utils";
import { FormActionButtonsComponent, FormCheckboxComponent, FormInputComponent } from "../../../shared/components";
import { UserRolesManagerComponent } from "../../../shared/components/user-roles-manager/user-roles-manager";
import { Constants } from "../../../shared/constants/constants";
import { AdaaBoolean, Language, PageMode } from "../../../shared/constants/enums";
import { UserRole, UserRolesPermission, ValidatorModelType } from "../../../shared/models";
import {
  LanguageService,
  SystemLayoutService,
  UserRolesApiService,
  ValidatorApiService,
} from "../../../shared/services";

@Component({
  selector: "adaa-user-roles-editor",
  imports: [
    FormInputComponent,
    ReactiveFormsModule,
    TranslatePipe,
    UserRolesManagerComponent,
    FormCheckboxComponent,
    FormActionButtonsComponent,
  ],
  templateUrl: "./user-roles-editor.html",
  styleUrl: "./user-roles-editor.scss",
})
export class UserRolesEditorComponent implements OnInit {
  private _formBuilder = inject(FormBuilder);
  private _userRolesApiService = inject(UserRolesApiService);
  private _router = inject(Router);
  private _toastrService = inject(ToastrService);
  private _systemLayoutService = inject(SystemLayoutService);
  private _validatorApiService = inject(ValidatorApiService);
  private _languageService = inject(LanguageService);

  private _untilDestroyed = AdaaHelper.untilDestroyed();

  pageMode = input.required<string>();
  id = input<number>();

  Language = Language;
  AdaaHelper = AdaaHelper;

  submitted = false;
  pmoOnly = signal<boolean>(false);
  userRolesForm: FormGroup;
  role = signal<UserRole | undefined>(undefined);
  permissions = signal<UserRolesPermission[]>([]);
  formValidation = signal<ValidatorModelType | undefined>(undefined);

  public get displayLabel(): string {
    const nameEN = this.userRolesForm.get("nameEN")?.value;
    const nameAE = this.userRolesForm.get("nameAE")?.value;

    return AdaaHelper.getPageLabel(nameAE, nameEN);
  }

  isViewMode = computed(() => this.pageMode() === PageMode.view);

  filteredPermissions = computed(() => {
    return this.permissions().filter((permission) =>
      this.pmoOnly() ? permission.pmoOnly === AdaaBoolean.Y : permission.pmoOnly === AdaaBoolean.N
    );
  });

  constructor() {
    effect(() => this._handlePmoRoleChange());
    effect(() => this._handleNotAllowExternal());
    effect(() => this._handleNotAllowParticipating());
  }

  public ngOnInit() {
    this._prepareForm();
    this._initPage();

    this._systemLayoutService.hasActiveEntityChanged$.pipe(this._untilDestroyed()).subscribe(() => {
      this._router.navigateByUrl("console");
    });
  }

  private _initPage() {
    this._userRolesApiService.getPermissions().subscribe({
      next: (response) => {
        if (!response.inError) this.permissions.set(response.responseData);
      },
      complete: () => {
        const id = this.id();
        if (id) this._getRole(id);
      },
    });
  }

  private _getRole(id: number) {
    this._userRolesApiService.getRole(id).subscribe({
      next: (response) => {
        this.userRolesForm.patchValue(response.responseData);
        this.role.set(response.responseData);

        this.userRolesForm.markAsPristine();
        this.userRolesForm.markAsUntouched();

        const map = new Map(response.responseData.permissions?.map((item) => [item.itemTypeId, item]));
        const res = this.permissions().map((item) => map.get(item.itemTypeId) || item);
        this.permissions.set(res);
      },
    });
  }

  private _prepareForm() {
    this.userRolesForm = this._formBuilder.group({
      nameAE: { value: null, disabled: this.isViewMode() },
      nameEN: { value: null, disabled: this.isViewMode() },
      dscAE: { value: null, disabled: this.isViewMode() },
      dscEN: { value: null, disabled: this.isViewMode() },
      enabled: { value: AdaaBoolean.Y, disabled: this.isViewMode() },
      isPmoRole: { value: AdaaBoolean.N, disabled: this.isViewMode() },
      allowExternal: { value: AdaaBoolean.Y, disabled: this.isViewMode() },
      allowParticipating: { value: AdaaBoolean.Y, disabled: this.isViewMode() },
    });

    this._validatorApiService
      .applyValidationToForm(Constants.VALIDATORS_CONF_KEY.VALIDATION_USERS_ROLES, this.userRolesForm)
      .subscribe({
        next: (response) => {
          this.formValidation.set(response.validation);
          this.userRolesForm = response.form;
        },
      });
  }

  private _handlePmoRoleChange() {
    const isPmoRole = this.permissions().some(
      (item) =>
        item.pmoOnly === AdaaBoolean.Y &&
        (item.create === AdaaBoolean.Y ||
          item.view === AdaaBoolean.Y ||
          item.manage === AdaaBoolean.Y ||
          item.execute === AdaaBoolean.Y)
    );

    //ON PERMISSION CHANGES MAKE SURE TO SET isPmoRole AS Y IF ANY PMO RULE SELECTED
    if (!this.isViewMode()) {
      if (isPmoRole) {
        this.userRolesForm.get("isPmoRole")?.patchValue(AdaaBoolean.Y);
        this.userRolesForm.get("isPmoRole")?.disable();
      } else {
        this.userRolesForm.get("isPmoRole")?.enable();
      }
    }
  }

  private _handleNotAllowExternal() {
    const notAllowExternal = this.permissions().some(
      (item) =>
        item.allowExternal === AdaaBoolean.N &&
        (item.create === AdaaBoolean.Y ||
          item.view === AdaaBoolean.Y ||
          item.manage === AdaaBoolean.Y ||
          item.execute === AdaaBoolean.Y)
    );

    if (!this.isViewMode()) {
      if (notAllowExternal) {
        this.userRolesForm.get("allowExternal")?.patchValue(AdaaBoolean.N);
        this.userRolesForm.get("allowExternal")?.disable();
      } else {
        this.userRolesForm.get("allowExternal")?.enable();
      }
    }
  }

  private _handleNotAllowParticipating() {
    const notAllowParticipating = this.permissions().some(
      (item) =>
        item.allowParticipating === AdaaBoolean.N &&
        (item.create === AdaaBoolean.Y ||
          item.view === AdaaBoolean.Y ||
          item.manage === AdaaBoolean.Y ||
          item.execute === AdaaBoolean.Y)
    );

    if (!this.isViewMode()) {
      if (notAllowParticipating) {
        this.userRolesForm.get("allowParticipating")?.patchValue(AdaaBoolean.N);
        this.userRolesForm.get("allowParticipating")?.disable();
      } else {
        this.userRolesForm.get("allowParticipating")?.enable();
      }
    }
  }

  public setPermissions(permission: UserRolesPermission) {
    const permissions: UserRolesPermission[] = AdaaHelper.clone(this.permissions());
    const index = permissions.findIndex((item) => item.id === permission.id);

    if (index !== -1) {
      permissions[index] = permission;
      this.permissions.set(permissions);
    }
  }

  public selectAll(event: Event, value: string) {
    const inputElement = event.target as HTMLInputElement;
    const isChecked = inputElement.checked;

    const permissions: UserRolesPermission[] = AdaaHelper.clone(this.permissions());

    permissions.forEach((permission) => {
      if (this.pmoOnly() ? permission.pmoOnly === AdaaBoolean.Y : permission.pmoOnly === AdaaBoolean.N) {
        switch (value) {
          case "create":
            if (permission.createEnabled === AdaaBoolean.Y) {
              permission.create = isChecked ? AdaaBoolean.Y : AdaaBoolean.N;
            }
            if (permission.viewEnabled === AdaaBoolean.Y) {
              permission.view = isChecked ? AdaaBoolean.Y : AdaaBoolean.N;
            }
            if (permission.manageEnabled === AdaaBoolean.Y) {
              permission.manage = isChecked ? AdaaBoolean.Y : AdaaBoolean.N;
            }
            break;
          case "view":
            if (permission.viewEnabled === AdaaBoolean.Y) {
              permission.view = isChecked ? AdaaBoolean.Y : AdaaBoolean.N;
            }
            break;
          case "manage":
            if (permission.manageEnabled === AdaaBoolean.Y) {
              permission.manage = isChecked ? AdaaBoolean.Y : AdaaBoolean.N;
            }
            if (permission.viewEnabled === AdaaBoolean.Y) {
              permission.view = isChecked ? AdaaBoolean.Y : AdaaBoolean.N;
            }
            break;
          case "execute":
            if (permission.executeEnabled === AdaaBoolean.Y) {
              permission.execute = isChecked ? AdaaBoolean.Y : AdaaBoolean.N;
            }
            break;
          case "dataEntry":
            if (permission.dataEntryEnabled === AdaaBoolean.Y) {
              permission.dataEntry = isChecked ? AdaaBoolean.Y : AdaaBoolean.N;
            }
            break;
          default:
            break;
        }
      }
    });

    this.permissions.set(permissions);
  }

  public submit() {
    this.submitted = true;

    if (this.userRolesForm.invalid) {
      this._toastrService.error(this._languageService.translate("common.form.label.missing_information"));
      return;
    }

    const role = {
      ...this.role(),
      ...this.userRolesForm.getRawValue(),
      permissions: this.permissions(),
      isPmoOnly: AdaaBoolean.N,
      status: Constants.OBJECT_STATUS.ACTIVE,
    };

    this._userRolesApiService.saveRole(this.pageMode() === PageMode.edit, role).subscribe({
      next: (response) => {
        if (response.inError) return;
        this._toastrService.success(this._languageService.translate("notification.success.save"));
        this._router.navigateByUrl("/console/users-roles");
      },
    });
  }

  public close() {
    this._router.navigateByUrl("/console/users-roles");
  }
}
